import {
  AllUnitsDocument,
  useCreateUnitMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {FullUnitFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type CreateUnitModalUserProps = {
  /** Receives the created unit, so pickers can select it immediately. */
  onSuccess?: (unit: FullUnitFragment) => void;
};

const DECIMAL_CHOICES = [0, 1, 2] as const;

export const CreateUnitModal = ({
  onRequestClose,
  onSuccess,
}: CreateUnitModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [name, setName] = React.useState('');
  const [singular, setSingular] = React.useState('');
  const [plural, setPlural] = React.useState('');
  const [pluralEdited, setPluralEdited] = React.useState(false);
  const [displayDecimals, setDisplayDecimals] = React.useState(0);
  const [description, setDescription] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const [createUnit, {loading}] = useCreateUnitMutation({
    refetchQueries: [{query: AllUnitsDocument}],
  });

  // "pass" → "passes" is close enough for a prefill; any edit takes over.
  const handleSingularChange = (value: string) => {
    setSingular(value);
    if (!pluralEdited) {
      setPlural(value === '' ? '' : `${value}s`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await createUnit({
        variables: {
          name: name.trim(),
          singular: singular.trim(),
          plural: plural.trim(),
          displayDecimals,
          description: description.trim() || null,
        },
      });
      const unit = result.data?.createUnit;
      if (unit) {
        onSuccess?.(unit);
      }
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !name.trim() || !singular.trim() || !plural.trim()}
      >
        Create
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create Unit"
      footer={footer}
      size="lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="e.g., Court 16 drills passes"
            autoFocus
          />
          <p className="text-muted-foreground text-xs">
            Shown where accounts list their currency. Freely renamable later —
            transactions store an internal id, never the name.
          </p>
        </div>

        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-sm font-medium">Singular</label>
            <TextInput
              value={singular}
              onChange={handleSingularChange}
              placeholder="pass"
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-sm font-medium">Plural</label>
            <TextInput
              value={plural}
              onChange={(value) => {
                setPlural(value);
                setPluralEdited(true);
              }}
              placeholder="passes"
            />
          </div>
        </div>
        <p className="text-muted-foreground -mt-2 text-xs">
          Amounts render with these labels: "1 pass", "3 passes".
        </p>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Decimals</label>
          <div className="flex gap-2">
            {DECIMAL_CHOICES.map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => setDisplayDecimals(choice)}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  displayDecimals === choice
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {(3).toFixed(choice)}
              </button>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">
            How many decimal places amounts show and accept. Whole-unit things
            like passes want 0.
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={description}
            onChange={setDescription}
            placeholder="Optional: what this unit tracks (e.g. 5-pack of drills classes at Court 16)"
            rows={2}
          />
        </div>
      </form>
    </Modal>
  );
};
